import * as React from "react";
import { observer, MobXProviderContext } from "mobx-react";
import { UitkButtonToggle } from "uitk-react-button-toggle";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { FlexComponent } from "src/typings/flexFramework/FlexComponent";
import { FlexComponentNameToWizardStateConfig } from "components/flexComponents/StorefrontWizardRegion/FlexComponentNameToWizardStateConfig";
import { LobNavigationProps } from "components/flexComponents/StorefrontWizardRegion/LobNavigation";
import { QueryObject, RouteStore } from "bernie-core";
import IsomorphicCarousel from "src/components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { Navigation } from "src/stores/wizard/config";
import { ClientWizardStateStore } from "src/stores/wizard/ClientWizardStateStore";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { useLocalization } from "@shared-ui/localization-context";

export const LobNavigation: React.FunctionComponent<LobNavigationProps> = observer(({ lobs }) => {
  const wizardState: ClientWizardStateStore = React.useContext(MobXProviderContext).wizardState;
  const routeStore: RouteStore = React.useContext(MobXProviderContext).routeStore;
  const keyHelper = new ItemKeyHelper("storefrontWizardNavigation");
  const defaultLobNavConfig: Navigation = {
    tabIcon: "help",
    tabTitleToken: ""
  };

  if (lobs.length === 1) {
    // No need for navigation in a single LOB pages
    return null;
  }

  // Check the route store, if there is no LOB query attached, get it from the wizardState default and redirect
  if (!routeStore.queryParams.pwaLob) {
    if (wizardState.useDefaultLOB) {
      routeStore.updateSearch({ pwaLob: wizardState.defaultLob });
      wizardState.setUseDefaultLOB(false);
    } else {
      if (typeof window !== "undefined" && window.history) {
        window.history.back();
      }
    }
  }

  const lobQueryCombiner = (prevQuery: QueryObject, nextQuery: QueryObject): QueryObject => {
    delete prevQuery.pwaSubLOB;

    return {
      ...prevQuery,
      ...nextQuery
    };
  };

  const handleLobChange = (lobName: string, model: any) => () => {
    if (!!model.directLink) {
      window.location.assign(model.directLink);

      return;
    }

    let queryParams: any = { pwaLob: lobName };
    if (!!wizardState.lobStateMap[lobName].subLOBState) {
      queryParams = {
        pwaLob: lobName,
        pwaSubLOB: wizardState.lobStateMap[lobName].subLOBState
      };
    }
    routeStore.updateSearch(queryParams, {}, false, lobQueryCombiner);
  };

  const isTabChecked = (lobName: string): boolean => wizardState.activeLob === lobName;
  const { formatText } = useLocalization();

  const getLobIcon = (lobName: string): string => {
    // If there is no config for the lob it will use help icon as default
    let lobIcon = defaultLobNavConfig.tabIcon;
    if (wizardState[FlexComponentNameToWizardStateConfig[lobName]]) {
      lobIcon = wizardState[FlexComponentNameToWizardStateConfig[lobName]].config.navigation.tabIcon;
    }

    return lobIcon;
  };

  const getLabel = (lobName: string): string => {
    let label = defaultLobNavConfig.tabTitleToken;
    if (wizardState[FlexComponentNameToWizardStateConfig[lobName]]) {
      label = formatText(wizardState[FlexComponentNameToWizardStateConfig[lobName]].config.navigation.tabTitleToken);
    }

    return label;
  };

  return (
    <Viewport>
      <ViewSmall>
        <div role="tablist" className="all-b-margin-six" data-testid="lob-navigation">
          <IsomorphicCarousel
            itemsVisible={{
              lg: 0,
              md: 0,
              sm: 3
            }}
            pageBy={1}
            buttonText={{
              nextButton: formatText("carousel.item.next"),
              prevButton: formatText("carousel.item.prev")
            }}
            peekWidth={20}
          >
            {lobs.map((flexComponent: FlexComponent, index) => (
              <UitkLayoutFlexItem grow={1} key={keyHelper.next()}>
                <div aria-label={getLabel(flexComponent.name)}>
                  <UitkButtonToggle
                    checked={isTabChecked(flexComponent.name)}
                    icon={getLobIcon(flexComponent.name)}
                    id={flexComponent.name}
                    label={getLabel(flexComponent.name)}
                    onChange={handleLobChange(flexComponent.name, flexComponent.model)}
                    aria-label={formatText(
                      "wizard.lob.tab.alttext",
                      getLabel(flexComponent.name),
                      index + 1,
                      lobs.length
                    )}
                    role="tab"
                    aria-selected={isTabChecked(flexComponent.name)}
                  />
                </div>
              </UitkLayoutFlexItem>
            ))}
          </IsomorphicCarousel>
        </div>
      </ViewSmall>
      <ViewMedium>
        <div data-testid="lob-navigation" role="tablist" className="all-b-margin-six">
          <UitkLayoutFlex tag="ul" space="three" className="no-bullet" data-testid="lob-navigation">
            {lobs.map((flexComponent: FlexComponent, index) => (
              <UitkLayoutFlexItem grow={1} key={keyHelper.next()}>
                <li aria-label={getLabel(flexComponent.name)}>
                  <UitkButtonToggle
                    checked={isTabChecked(flexComponent.name)}
                    icon={getLobIcon(flexComponent.name)}
                    id={flexComponent.name}
                    label={getLabel(flexComponent.name)}
                    onChange={handleLobChange(flexComponent.name, flexComponent.model)}
                    aria-label={formatText(
                      "wizard.lob.tab.alttext",
                      getLabel(flexComponent.name),
                      index + 1,
                      lobs.length
                    )}
                    role="tab"
                    aria-selected={isTabChecked(flexComponent.name)}
                  />
                </li>
              </UitkLayoutFlexItem>
            ))}
          </UitkLayoutFlex>
        </div>
      </ViewMedium>
    </Viewport>
  );
});
